import {deepCopy} from "../base/Utils";
import {type StyleRuleObject, type StyleSheet} from "../ui/Style";
import {lazyInit} from "./LazyInitialize";
import {type LegacyPropertyDescriptor} from "./Utils";

interface StyleRuleOptions {
    targetMethodName?: string;
    getKey?: (key: string | symbol) => string;
    inherit?: boolean;
    selector?: string;
}

interface StyleDescriptor extends LegacyPropertyDescriptor {
    objInitializer?: () => StyleRuleObject;
    initializer: (() => StyleRuleObject) | undefined;
}

type StyleRuleFunction = () => StyleRuleObject;

// What a @styleRule is written with: the object, a function that builds it, or an array merged into one.
// The name is local: `StyleRuleValue` in ui/Style.ts is the class name a rule reads back as, not this
type WrittenStyleRule = StyleRuleObject | StyleRuleFunction | (StyleRuleObject | StyleRuleFunction)[];

function evaluateStyleRuleObject(target: object, initializer: (() => StyleRuleObject) | undefined, value: WrittenStyleRule, _options: StyleRuleOptions): StyleRuleObject {
    let result = initializer ? initializer.call(target) : value;
    if (typeof result === "function") {
        result = result();
    }
    if (Array.isArray(result)) {
        result = Object.assign({}, ...result);
    }
    return result;
}

function getStyleRuleKey(key: string | symbol): string {
    return "__style__" + String(key);
}

function getKeyframesRuleKey(key: string | symbol): string {
    return "__keyframes__" + String(key);
}

export const PREFERRED_CLASS_NAME_KEY = Symbol("PreferredClassName");

function getPreferredClassName(cls: object, key: string | symbol, _descriptor: PropertyDescriptor): string {
    if (key !== "container") {
        return String(key);
    }
    let className = cls.constructor.name;
    if (className.endsWith("Style")) {
        className = className.substr(0, className.length - 5);
    }
    className = className.replaceAll("$", ""); // Fix minify mangling
    return className + "-container";
}

// TS1271 requires a legacy decorator to answer with void or any, so the return cannot be narrowed
type FakedDecoratorType = (target: object, key: string | symbol, descriptor?: PropertyDescriptor) => any;

// TODO: this function can be made a lot more generic, to wrap plain object initializer with inheritance support
function styleRuleWithOptions(...optionsArgs: StyleRuleOptions[]): FakedDecoratorType {
    let options: StyleRuleOptions = Object.assign({}, ...optionsArgs);
    // TODO: Remove this if you don't think it's appropiate, I thought a warning would do no harm
    if (!options.targetMethodName) {
        console.error("WARNING: targetMethodName not specified in the options (default is \"css\")");
    }
    let targetMethodName = options.targetMethodName || "css";

    function styleRuleDecorator(target: object, key: string | symbol, descriptor: StyleDescriptor): PropertyDescriptor {
        const {initializer, value} = descriptor;

        descriptor.objInitializer = function (this: StyleSheet) {
            let style = evaluateStyleRuleObject(this, initializer, value, options);

            if (options.selector) {
                style["selectorName"] = options.selector;
            }

            if (options.inherit) {
                // Get the value we set in the prototype of the parent class
                let parentDesc = Object.getPrototypeOf(target)[options.getKey!(key)];
                if (!parentDesc) {
                    console.error("You're trying to inherit a rule that isn't implemented in the parent: " + String(key));
                }
                let parentStyle = evaluateStyleRuleObject(this, parentDesc.objInitializer, parentDesc.value, options);
                style = deepCopy({}, parentStyle, style);
            }

            style[PREFERRED_CLASS_NAME_KEY] = getPreferredClassName(target, key, descriptor);

            return style;
        };

        // Change the prototype of this object to be able to access the old descriptor/value
        (target as any)[options.getKey!(key)] = {...descriptor};

        descriptor.initializer = function (this: StyleSheet) {
            let style = descriptor.objInitializer!.call(this);
            return (this as any)[targetMethodName](style);
        };

        delete descriptor.value;

        return lazyInit(target, key, descriptor);
    }

    return styleRuleDecorator as FakedDecoratorType;
}

// TODO: Second argument is mostly useless (implied from targetMethodName)
const styleRule = styleRuleWithOptions({
    targetMethodName: "css",
    getKey: getStyleRuleKey,
    inherit: false,
});

const styleRuleInherit = styleRuleWithOptions({
    targetMethodName: "css",
    getKey: getStyleRuleKey,
    inherit: true,
});

export function styleRuleCustom(options: StyleRuleOptions): FakedDecoratorType {
    return styleRuleWithOptions(Object.assign({
        targetMethodName: "css",
        getKey: getStyleRuleKey,
        inherit: false,
    }, options));
}

const keyframesRule = styleRuleWithOptions({
    targetMethodName: "keyframes",
    getKey: getKeyframesRuleKey,
    inherit: false,
});

// TODO: This is currently not working (I think)
const keyframesRuleInherit = styleRuleWithOptions({
    targetMethodName: "keyframes",
    getKey: getKeyframesRuleKey,
    inherit: true,
});

export {styleRule, styleRuleInherit, keyframesRule, keyframesRuleInherit, styleRuleWithOptions};

